import { useCallback, useEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { getGoogleChallenge, isNativeGoogleEnvironment, nativeGoogleSignIn } from '@/utils/googleSignIn';
import { WebGoogleButton } from './WebGoogleButton';

export const GoogleSignIn = ({ onSuccess, disabled, onBusyChange }) => {
  // WTN injects window.WTN / window.WebToNativeInterface AFTER the first React
  // render in some Android WebView builds. A plain synchronous check would
  // always return false, causing the web GIS popup button to render instead of
  // the native button — and popups are blocked inside WebViews.
  // Poll briefly so we catch any late injection within 5 seconds of mount.
  const [native, setNative] = useState(() => isNativeGoogleEnvironment());

  useEffect(() => {
    if (native) return; // already confirmed native — no need to poll
    const INTERVAL_MS = 200;
    const TIMEOUT_MS  = 5000;
    let elapsed = 0;
    const id = setInterval(() => {
      elapsed += INTERVAL_MS;
      if (isNativeGoogleEnvironment()) {
        setNative(true);
        clearInterval(id);
      } else if (elapsed >= TIMEOUT_MS) {
        clearInterval(id);
      }
    }, INTERVAL_MS);
    return () => clearInterval(id);
  }, [native]);
  const { googleLogin } = useAuth();
  const [challenge, setChallenge] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const mounted = useRef(true);
  const abort = useRef(null);
  const inFlight = useRef(false);

  const prepare = useCallback(async () => {
    try {
      const next = await getGoogleChallenge(native ? 'native' : 'web');
      if (mounted.current) setChallenge(next);
    } catch {
      if (mounted.current) setError('Unable to prepare Google sign-in. Please retry.');
    }
  }, [native]);

  useEffect(() => {
    mounted.current = true;
    if (!native) prepare();
    return () => { mounted.current = false; abort.current?.abort(); };
  }, [native, prepare]);

  const completeSignIn = async (credential, currentChallenge) => {
    if (!credential) throw new Error('Google did not return a sign-in credential. Please retry.');
    if (!mounted.current) return;
    const user = await googleLogin(credential, currentChallenge.challenge_id);
    if (mounted.current) onSuccess(user);
  };

  const run = async (credential) => {
    if (inFlight.current || disabled) return;
    inFlight.current = true;
    setBusy(true); onBusyChange(true); setError('');
    try {
      if (native) {
        abort.current = new AbortController();
        const next = await getGoogleChallenge('native');
        const token = await nativeGoogleSignIn(abort.current.signal);
        await completeSignIn(token, next);
      } else {
        await completeSignIn(credential, challenge);
      }
    } catch (err) {
      if (mounted.current) {
        const detail = err.response?.data?.detail;
        setError(typeof detail === 'string' ? detail : (err.message || 'Google sign-in failed. Please retry.'));
        if (!native) { setChallenge(null); prepare(); }
      }
    } finally {
      inFlight.current = false;
      if (mounted.current) { setBusy(false); onBusyChange(false); }
    }
  };

  return (
    <section className="space-y-3" data-testid="auth-google-section" aria-label="Google sign-in">
      {native ? (
        <Button type="button" onClick={() => run()} disabled={disabled || busy} className="w-full h-12 bg-white text-black hover:bg-white/90 rounded-xl font-medium gap-3" data-testid="auth-google-native-button">
          {busy && <Loader2 className="animate-spin" size={18} aria-hidden="true" />}
          {busy ? 'Signing in…' : 'Continue with Google'}
        </Button>
      ) : challenge ? (
        <WebGoogleButton nonce={challenge.nonce} onCredential={run} disabled={disabled || busy} onError={() => { setError('Google sign-in was cancelled or failed. Please retry.'); setChallenge(null); prepare(); }} />
      ) : (
        <Button type="button" variant="outline" className="w-full h-12 rounded-xl" disabled={!error} onClick={() => { setError(''); prepare(); }} data-testid="auth-google-prepare-button">
          {error ? 'Retry Google sign-in' : 'Loading Google sign-in…'}
        </Button>
      )}
      {busy && <p role="status" className="text-sm text-white/60 text-center" data-testid="auth-google-status">Connecting your account…</p>}
      {busy && native && <button type="button" className="block mx-auto min-h-11 text-sm text-white/70 underline" onClick={() => abort.current?.abort()} data-testid="auth-google-cancel-button">Cancel</button>}
      {error && <p role="alert" className="text-sm text-red-300 break-words" data-testid="auth-google-error">{error}</p>}
    </section>
  );
};